import React, { useEffect, useMemo, useRef, useState } from 'react';
import _get from 'lodash/get';
import makeStyles from '@material-ui/core/styles/makeStyles';
import classnames from 'classnames';
import { useHistory } from 'react-router';
import { useData } from 'resift';
import makePath from 'helpers/makePath';
import makeGetEntityTypeStatus from 'fetches/makeGetEntityTypeStatus';
import useSyncInfo from 'helpers/useSyncInfo';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import LoadingButton from 'atoms/LoadingButton';
import NoPagesIcon from 'components/Icons/NoPages';
import CreatingFilesIcon from 'components/Icons/CreatingFiles';

interface Props {
  className?: string;
  migrationInProgress?: boolean;
  pageTypeIsSyncManaged: boolean;
  isOpenRolePageType: boolean;
  entityTypeId: string;
  onClick: () => void;
}

function NoPagesView({
  className,
  migrationInProgress = false,
  pageTypeIsSyncManaged,
  isOpenRolePageType,
  entityTypeId,
  onClick,
}: Props) {
  const classes = useStyles();
  const history = useHistory();
  const reloadTimeoutRef = useRef<number | null>(null);
  const [reloadLoading, setReloadLoading] = useState(false);
  const getLastSyncStatus = useMemo(() => makeGetEntityTypeStatus(entityTypeId), [entityTypeId]);
  const lastSyncStatusInfo = useData(getLastSyncStatus);
  const syncInfo = useSyncInfo(_get(lastSyncStatusInfo, ['syncStatus']));
  const pageCsvUploadInProgress = syncInfo.syncRunning;
  const isCreatingPages = migrationInProgress || pageCsvUploadInProgress;
  const title = isCreatingPages ? 'Pages are being created' : 'No pages have been created yet';
  const buttonTitle = isCreatingPages ? 'Reload' : 'Create pages manually';
  const isReloadButton = buttonTitle === 'Reload';
  const subtitle = migrationInProgress
    ? 'Pages are being created from an existing attribute. Check back in a few minutes'
    : pageCsvUploadInProgress
    ? 'Pages are being created from your CSV upload'
    : "You haven't created any pages yet. You can create pages by...";

  useEffect(
    () => () => {
      if (reloadTimeoutRef.current) {
        window.clearTimeout(reloadTimeoutRef.current);
      }
    },
    [],
  );

  const handleNewPagesClick = () => {
    if (isReloadButton) {
      setReloadLoading(true);
      if (reloadTimeoutRef.current) {
        window.clearTimeout(reloadTimeoutRef.current);
      }
      reloadTimeoutRef.current = window.setTimeout(() => {
        setReloadLoading(false);
      }, 1000);
    }
    onClick();
  };

  const icon = isCreatingPages ? (
    <CreatingFilesIcon
      data-testid="creating-files-icon"
      className={classnames(classes.creatingFilesIcon, classes.marginBottom)}
    />
  ) : (
    <NoPagesIcon
      data-testid="no-pages-icon"
      className={classnames(classes.noResultsIcon, classes.marginBottom)}
    />
  );

  const NewPagesButton = (
    <Tooltip
      arrow
      placement="top"
      title={isOpenRolePageType ? 'Go to the Org Chart to create Open Roles' : ''}
    >
      <div>
        <LoadingButton
          disabled={pageTypeIsSyncManaged || isOpenRolePageType}
          variant="outlined"
          loading={isReloadButton && reloadLoading}
          onClick={handleNewPagesClick}
          label={buttonTitle}
          className={classes.newPagesButton}
        />
      </div>
    </Tooltip>
  );

  const handleUploadCsvClick = () => {
    history.push(makePath('PageDataCsvSetup', { entityTypeKey: entityTypeId }));
  };

  return (
    <Card classes={{ root: classes.noResultsCard }} className={className}>
      <CardContent classes={{ root: classes.noResultsCardContent }}>
        {icon}
        <Typography className={classnames(classes.marginBottom, classes.title)}>{title}</Typography>
        <Typography className={classes.marginBottom} variant="subtitle2">
          {subtitle}
        </Typography>
        <div className={classes.buttonsRow}>
          {!pageTypeIsSyncManaged ? (
            NewPagesButton
          ) : (
            <Tooltip
              data-testid="tooltip"
              title="Your pages are being automatically managed via data sync"
              placement="top"
              arrow
            >
              <div data-testid="disabled-new-pages-button">{NewPagesButton}</div>
            </Tooltip>
          )}
          {!isCreatingPages && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleUploadCsvClick}
              className={classes.csvButton}
            >
              Create Pages from CSV
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const useStyles = makeStyles((theme: any) => ({
  noResultsCard: {
    marginTop: theme.spacing(3),
  },
  noResultsCardContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: 400,
    justifyContent: 'center',
  },
  creatingFilesIcon: {
    width: 130,
    height: 130,
  },
  noResultsIcon: {
    width: 130,
    height: 130,
  },
  marginBottom: {
    marginBottom: theme.spacing(3),
  },
  title: {
    ...theme.typography.body1bold,
  },
  buttonsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  csvButton: {
    textTransform: 'none',
  },
  newPagesButton: { marginRight: theme.spacing(3) },
}));

export default NoPagesView;
